import Grid from '@material-ui/core/Grid';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import React, {useCallback, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import styled from 'styled-components';
import {
    CmsDynamicMappingProps,
    GridContainer,
    Hyperlink,
    Typography,
    VerticalSpacer
} from '../../../elements';
import {
    FeaturesContentCard2UpProps,
    FeaturesContentCardList,
    FeaturesContentCardListProps,
    FeaturesContentCardOptionsArray,
    FeaturesContentCardProps
} from '../../../elements/features/features-content';
import {SearchField} from '../../../elements/inputs/search/SearchField';
import {MultiCard, MultiCardOptionsArray, MultiCardOptions} from '../../multicard';

const checkCardMatch = (
    filteredList: FeaturesContentCardOptionsArray,
    filterLower: string,
    element: FeaturesContentCardProps,
    item: CmsDynamicMappingProps<FeaturesContentCardProps>
) => {
    const {title = '', description = ''} = element;
    if (
        title.toLowerCase().indexOf(filterLower) > -1 ||
        description.toLowerCase().indexOf(filterLower) > -1
    ) {
        filteredList.push(item);
    }
};

type ElementOptions = FeaturesContentCardProps | FeaturesContentCard2UpProps;

const getFilteredCards = (cardList: FeaturesContentCardOptionsArray, filterStr = '') => {
    if (!filterStr) {
        return cardList;
    }

    const filterLower = filterStr.toLowerCase();
    const filteredList: FeaturesContentCardOptionsArray = [];

    cardList.forEach((item) => {
        const itemLength = Object.keys(item).length;
        if (item && itemLength) {
            for (const key in item) {
                if (Object.prototype.hasOwnProperty.call(item, key)) {
                    const element: ElementOptions = item[key];
                    const twoupElement = element as FeaturesContentCard2UpProps;
                    if (twoupElement && twoupElement.cards && twoupElement.cards.length) {
                        twoupElement.cards.forEach((el) => {
                            checkCardMatch(filteredList, filterLower, el, item);
                        });
                    } else {
                        checkCardMatch(filteredList, filterLower, element, item);
                    }
                }
            }
        }
    });

    return filteredList;
};

const Styled = styled.div`
    .hlink-wrap {
        color: ${(p) => p.theme.palette.text.secondary};
        opacity: 0.5;
        transition: opacity 0.25s ${(p) => p.theme.easing};
        &.active,
        &:hover {
            opacity: 1;
        }
    }
    a.ue-link-wrap {
        white-space: initial;
        color: ${(p) => p.theme.palette.text.secondary};
    }
    .multicard-grid-container {
        padding: 0;
    }

    .heading.h5 {
        text-transform: initial;
    }
`;

const HyperlinksBar = styled.div`
    .hlink-wrap {
        color: ${(p) => p.theme.palette.text.secondary};
        opacity: 0.5;
        transition: opacity 0.25s ${(p) => p.theme.easing};
        &.active,
        &:hover {
            opacity: 1;
        }
    }
    .heading.h5 {
        text-transform: initial;
    }
    ${(p) => p.theme.breakpoints.down('sm')} {
        &.linksbar-collapsed,
        &.linksbar-expanded {
            position: fixed;
            top: 0px;
            left: 0px;
            background-color: ${(p) =>
                p.theme.ifLight(
                    p,
                    p.theme.palette.neutral.pearl,
                    p.theme.palette.neutral.concrete
                )};
            z-index: 3;
            box-sizing: border-box;
            width: 100%;
            text-align: center;
            padding-top: ${(p) => p.theme.spacing(2)}px;
            padding-bottom: ${(p) => p.theme.spacing(2)}px;
        }
    }

    a.ue-link-wrap {
        white-space: initial;
        color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.neutral.steel, p.theme.palette.neutral.silver)};
        &:hover {
            color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.common.black, p.theme.palette.common.white)};
        }
    }
`;

interface SectionProps extends FeaturesContentCardListProps {
    title?: string;
}

export interface FilteredFeaturesContentSection {
    title?: string;
    noResults?: string;
    multiCard?: MultiCardOptions[];
}
export interface FeaturesContentSectionProps {
    searchLabelText?: string;
    sections?: SectionProps[];
    filtered?: FilteredFeaturesContentSection;
    accentColor?: AccentColorOptions;
}

export const FeaturesContentSection = ({
    searchLabelText,
    sections = [],
    filtered = {},
    accentColor
}: FeaturesContentSectionProps): JSX.Element | null => {
    const isSm = useBreakpointDown('sm');
    const [HyperlinksBarCollapsed, setHyperlinksBarCollapsed] = useState(true);
    const toggleHyperlinksBarCollapse = (e) => {
        e.preventDefault();
        setHyperlinksBarCollapsed(!HyperlinksBarCollapsed);
    };
    const {ref, inView} = useInView();

    const filteredViewTitle = filtered.title || 'Search results';
    let noResults = false;

    const firstSection = sections[0] || {};
    const [searchText, setSearchText] = useState('');
    const filteredView = Boolean(searchText && searchText.length);
    const [activeSection, setActiveSection] = useState<SectionProps>(firstSection);
    const clickSectionTab = useCallback(
        (section) => {
            if (!section) return;
            setActiveSection(section);
        },
        [setActiveSection]
    );

    if (!sections.length) {
        return null;
    }

    let masterCardList: FeaturesContentCardOptionsArray = [];
    let filteredCardList = activeSection?.cardList || [];
    if (filteredView) {
        sections.forEach((section) => {
            const cardList = section?.cardList || [];
            masterCardList = masterCardList.concat(cardList);
        });

        filteredCardList = getFilteredCards(masterCardList, searchText);
        noResults = !filteredCardList || filteredCardList.length === 0;
    }

    const multiCardArray: MultiCardOptionsArray = [];
    if (filtered.multiCard && filtered.multiCard.length) {
        filtered.multiCard.forEach((card) => {
            const dynamicObject: CmsDynamicMappingProps<MultiCardOptions> = {
                text: {
                    ...card
                }
            };
            multiCardArray.push(dynamicObject);
        });
    }

    return (
        <Styled>
            <GridContainer>
                <Grid container spacing={2}>
                    <Grid ref={ref} item xs={12}>
                        <SearchField
                            variant="large"
                            accentColor={accentColor}
                            searchLabelText={searchLabelText}
                            onChange={(searchText) => {
                                setSearchText(searchText);
                            }}
                        />
                        <VerticalSpacer variant="custom" spacing={7} downSmSpacing={3} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        {isSm && !inView ? (
                            HyperlinksBarCollapsed ? (
                                <HyperlinksBar className="linksbar-collapsed">
                                    <Typography variant="h5">
                                        <Hyperlink
                                            accentColor={accentColor}
                                            href={'/features'}
                                            component="a"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                toggleHyperlinksBarCollapse(e);
                                            }}>
                                            {activeSection.title}
                                        </Hyperlink>
                                    </Typography>
                                </HyperlinksBar>
                            ) : (
                                <HyperlinksBar className="linksbar-expanded">
                                    {sections?.map((section, i) => (
                                        <React.Fragment key={`faq-search-section-xs-${i}`}>
                                            <Typography variant="h5">
                                                <Hyperlink
                                                    accentColor={accentColor}
                                                    active={
                                                        filteredView
                                                            ? false
                                                            : section.title === activeSection.title
                                                    }
                                                    key={i}
                                                    href={'/faq'}
                                                    component="a"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        toggleHyperlinksBarCollapse(e);
                                                        clickSectionTab(section);
                                                    }}>
                                                    {section.title}
                                                </Hyperlink>
                                            </Typography>
                                            {i !== sections?.length - 1 ? (
                                                <VerticalSpacer variant="eyebrow" />
                                            ) : (
                                                ''
                                            )}
                                        </React.Fragment>
                                    ))}
                                </HyperlinksBar>
                            )
                        ) : (
                            sections.map((section, i) => {
                                return (
                                    <React.Fragment key={`features-content-section-${i}`}>
                                        <Typography variant="h5">
                                            <Hyperlink
                                                key={i}
                                                href={'/features'}
                                                component="a"
                                                active={section.title === activeSection.title}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    clickSectionTab(section);
                                                }}>
                                                {section.title}
                                            </Hyperlink>
                                        </Typography>
                                        {i !== sections.length - 1 ? (
                                            <VerticalSpacer variant="eyebrow" />
                                        ) : (
                                            ''
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </Grid>
                    <Grid item xs={false} md={1}></Grid>
                    <Grid item xs={12} md={8}>
                        <FeaturesContentCardList
                            title={filteredView ? filteredViewTitle : activeSection?.title}
                            cardList={filteredCardList}
                        />
                        {noResults ? (
                            <>
                                <Typography variant="p">
                                    {filtered.noResults || 'No results'}
                                </Typography>
                                <VerticalSpacer variant="section" />
                                <MultiCard cards={multiCardArray} />
                                <VerticalSpacer variant="section" />
                            </>
                        ) : (
                            ''
                        )}
                    </Grid>
                </Grid>
            </GridContainer>
        </Styled>
    );
};
